// kanbanRowValues — valeurs dérivées d'une tâche, partagées par la liste (tri
// des colonnes) et le board (recherche plein texte).
//
// Module SANS composant, délibérément : ces helpers ne peuvent pas être
// exportés depuis un fichier de composants sans casser la frontière Fast
// Refresh (règle ESLint react-refresh/only-export-components).
//
// Règle commune aux deux usages : on lit les LIBELLÉS AFFICHÉS (état résolu par
// le workflow, nom d'utilisateur, libellés de tags), jamais les identifiants
// bruts stockés sur la tâche — trier ou chercher sur un TID ne correspondrait à
// rien de ce que l'utilisateur voit.

import { getStateLabel } from './kanbanWorkflowStore';

/** Libellé d'une option (type, type de retour, tag…) depuis sa valeur brute. */
export const resolveOptionLabel = (value, options) => {
  if (value == null || value === '' || !Array.isArray(options)) return '';
  const match = options.find(o => String(o.value) === String(value));
  return match ? match.label : String(value);
};

/** Nom affiché de l'assigné (stocké tantôt en id utilisateur, tantôt en nom). */
export const resolveUserName = (assignee, users = []) => {
  const user = users.find(u => String(u.id) === String(assignee) || u.name === assignee);
  return user?.name ?? (typeof assignee === 'string' ? assignee : '');
};

/** Libellés des tags de la tâche, dans leur ordre de stockage. */
export const resolveTagLabels = (task, tagOptions = []) => (task.data.tags ?? [])
  .map(tid => resolveOptionLabel(tid, tagOptions) || String(tid));

/**
 * Titres des contenus liés de la tâche (entreprise, transaction, contact…),
 * dans leur ordre de stockage — ce que la colonne « Contenu lié » affiche, donc
 * ce sur quoi elle trie.
 */
export const resolveLinkedContentTitles = (task) => (task.data.linkedContentRefs ?? [])
  .map(ref => ref?.title)
  .filter(Boolean);

/**
 * Libellé de statut, même cascade que StatusCell (KanbanListRow) : le workflow
 * PROPRE à la ligne d'abord, puis le titre de colonne du board, puis
 * l'identifiant d'état brut.
 */
export const resolveStatusLabel = (task, columns = []) =>
  getStateLabel(task.data.type, task.data.state)
  || columns.find(c => String(c.id) === String(task.data.columnId))?.title
  || (task.data.state ? String(task.data.state) : '');

/** Insensible à la casse ET aux accents : « Prêt » se trouve en tapant « pret ». */
export const normalizeSearch = (v) => String(v ?? '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

/**
 * Tout le texte visible d'une ligne / carte : titre, titre du parent, type,
 * statut, assigné, type de retour, tags, contenus liés.
 */
export const taskSearchHaystack = (task, ctx = {}) => normalizeSearch([
  task.data.title,
  ctx.parentTitle,
  resolveOptionLabel(task.data.type, ctx.typeOptions),
  resolveStatusLabel(task, ctx.columns),
  resolveUserName(task.data.assignee, ctx.users),
  resolveOptionLabel(task.data.demande_evolution, ctx.returnTypeOptions),
  ...resolveTagLabels(task, ctx.tagOptions),
  ...resolveLinkedContentTitles(task),
].filter(Boolean).join(' '));

/**
 * Date de mise à jour de la tâche (`changed`).
 *
 * L'API la sérialise via decoupled_timestamp / custom_date_format
 * 'Y-m-d\TH:i:s' : une CHAÎNE en heure du site, sans fuseau ("2026-08-27T12:13:44")
 * — jamais un entier. La branche numérique ne couvre qu'un display reconfiguré
 * pour émettre le timestamp UNIX brut (même précaution que `archiveTimestamp`
 * dans services/archive, où l'oubli vidait silencieusement la liste).
 */
export const changedTimestamp = (changed) => {
  if (changed == null || changed === '') return 0;
  const ms = /^\d+$/.test(String(changed))
    ? Number(changed) * 1000
    : new Date(changed).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/** Libellé de la colonne « Mise à jour » : date + heure ; '' si inexploitable. */
export const formatChangedDateTime = (changed) => {
  const ms = changedTimestamp(changed);
  return ms ? new Date(ms).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '';
};

/** Infobulle : le même instant à la seconde près. */
export const formatChangedPrecise = (changed) => {
  const ms = changedTimestamp(changed);
  return ms ? new Date(ms).toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'medium' }) : '';
};

// ── Échéance (field_due_date) ───────────────────────────────────────────────
//
// Le champ est une DATE SEULE : l'API la sérialise en 'YYYY-MM-DD' (parfois
// suivie d'une heure). Tout se compare donc en CHAÎNES 'YYYY-MM-DD', jamais en
// objets Date — c'est déjà la convention du back (`userTasks` bucketise avec
// `$due_date < $today`), et ça évite le décalage d'un jour que provoquerait
// `new Date('2026-09-02')` (interprété en UTC) une fois reformaté en local.

/** Date du jour en 'YYYY-MM-DD', en heure LOCALE (toISOString donnerait l'UTC). */
export const todayIso = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** 'YYYY-MM-DD' décalé de N jours / N mois à partir d'aujourd'hui. */
export const isoFromToday = ({ days = 0, months = 0 } = {}) => {
  const d = new Date();
  if (months) d.setMonth(d.getMonth() + months);
  if (days) d.setDate(d.getDate() + days);
  return todayIso(d);
};

/** Partie date d'une échéance ('' si absente) — le reste (heure) est ignoré. */
export const dueDateIso = (dueDate) => String(dueDate ?? '').slice(0, 10);

/** Échéance dépassée = strictement AVANT aujourd'hui (le jour même n'est pas en retard). */
export const isOverdue = (dueDate, today = todayIso()) => {
  const iso = dueDateIso(dueDate);
  return !!iso && iso < today;
};

/** Libellé d'échéance (« 2 septembre 2026 ») ; '' si aucune date exploitable. */
export const formatDueDate = (dueDate) => {
  const iso = dueDateIso(dueDate);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  // Construction par composants : une date-seule reste le jour affiché quel que
  // soit le fuseau du navigateur.
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('fr-FR', { dateStyle: 'long' });
};

/** Clé de tri de l'échéance : 'YYYY-MM-DD' se trie déjà lexicographiquement. */
export const dueDateSortKey = (dueDate) => dueDateIso(dueDate);
